import { z } from 'zod';
import {
  ProductIntlSchema,
  productSchema,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/product.ts';
import { errorMessage } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import {
  FUNDING_GOAL_AMOUNT__MAX,
  MAX_PURCHASE_PER_USER__MAX,
  ProductType,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { StoreProductQnaType } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/StoreProduct/StoreProduct.ts';
import { GOODS_TYPE } from '@services/MarppleShop/features/Product/type';
import { Validator } from '@shared/validators';
import { isNull } from '@fxts/core';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';

const dateSchema = z.union([z.date(), z.null()]).nullable().optional();
export const categoryCodeSchema = z
  .string({
    required_error: errorMessage.select_category,
    invalid_type_error: errorMessage.select_category,
  })
  .min(1, { message: errorMessage.select_category });

const tplFeeSchema = z.object({
  ratio: z.number().min(0).max(100).optional(),
  before_ratio: z.number().min(0).max(100).optional(),
});

export const storesProductBaseSchema = z.object({
  id: z.number().optional(),
  store_id: z.number(),
  product_id: z.number().optional(),
  is_prohibited: z.boolean().optional(),
  is_solution: z.boolean().optional(),
  is_force_out_of_stock: z.boolean().default(false),
  sp_cate_root_id: z.number().nullable().default(null),
  sp_cate_list_id: z.number().nullable().default(null),
  sp_cate_item_id: z.number().nullable().default(null),
  sp_cate_item_code: categoryCodeSchema,
  sp_cate_list_code: categoryCodeSchema,
  sp_cate_root_code: categoryCodeSchema,
  membership_ids: z.array(z.string()).nullable(),
  max_purchase_per_user: z
    .number()
    .int()
    .max(MAX_PURCHASE_PER_USER__MAX, { message: errorMessage.max_purchase_per_user })
    .nullable()
    .default(null),
  sell_start_at: dateSchema,
  sell_end_at: dateSchema,
  meta: z.object({
    limited: z.boolean().default(false),
    adult: z.boolean().default(false),
    low_stock_threshold: z
      .enum(['THRESHOLD_10', 'THRESHOLD_100', 'THRESHOLD_INFINITY'])
      .nullable()
      .default(null),
    funding: z
      .object({
        target_amount: z
          .number()
          .int()
          .max(FUNDING_GOAL_AMOUNT__MAX, {
            message: errorMessage.goal_amount,
          })
          .nullable()
          .default(null),
        description: z.string().nullable().default(null),
      })
      .nullable()
      .default(null),
  }),
  product_basic_info: z.string().optional().nullable(),
  product_basic_info_en: z.string().optional().nullable(),
  product_basic_info_jp: z.string().optional().nullable(),
  // 환불 정보
  refund_info: z.string().nullable().default(null),
  refund_info_en: z.string().nullable().default(null),
  refund_info_jp: z.string().nullable().default(null),
  // KC 인증
  is_kc_cert_checked: z.boolean().default(false),
  certification_info: z.string().nullable().default(null),
  // 상품 문의
  qna_type: z.nativeEnum(StoreProductQnaType).nullable().default(null),
  qna_link: z
    .string()
    .nullable()
    .default(null)
    .refine(
      (value) => {
        if (isNull(value)) return true;
        return Validator.url.isUrl(value) && UtilS.isUrl(value);
      },
      { message: errorMessage.invalid_qna_sns_link },
    ),
  // 의류 소재
  material: z
    .string()
    .nullable()
    .default(null)
    .refine(
      (value) => {
        if (isNull(value)) return true;
        return !Validator.regex.includeHangul(value);
      },
      { message: errorMessage.invalid_material },
    ),
  // 수익 비율
  seller_profit_share: z.number().min(0).max(100).optional(),
  // 배송 출발일 노출
  shipping_date_hidden: z.boolean().optional(),
});

const storeProductShipDateSchema = z
  .union([
    z.object({
      ship_date: z.string().nullable().default(null).optional(),
      store_product_id: z.number().int().nullable().default(null).optional(),
    }),
    z.object({}).strict(),
  ])
  .optional();

const CreatorProductBaseSchema = storesProductBaseSchema.extend({
  goods_type_id: z.nativeEnum(GOODS_TYPE).default(GOODS_TYPE.PBG),
  product_type_id: z.nativeEnum(ProductType).default(ProductType.ALWAYS),
  _: z.object({
    hs_code: z.string().nullable().default(null),
    product: productSchema,
    store_product_ship_date: storeProductShipDateSchema,
    tpl_fee: tplFeeSchema.optional(),
  }),
});

export const creatorProductSchema = CreatorProductBaseSchema;

export const creatorProductEditSchema = CreatorProductBaseSchema.extend({
  id: z.number(),
});

export const creatorProductTplSchema = creatorProductEditSchema.extend({
  shipping_date_hidden: z.boolean().default(false),
  _: z.object({
    hs_code: z.string().nullable().default(null),
    product: productSchema,
    tpl_fee: tplFeeSchema,
  }),
});

export const creatorProductTplIntlSchema = creatorProductEditSchema.extend({
  refund_info_en: z.string().nullable().default(null),
  refund_info_jp: z.string().nullable().default(null),
  certification_info_en: z.string().nullable().default(null),
  certification_info_jp: z.string().nullable().default(null),
  shipping_date_hidden: z.boolean().default(false),
  _: z.object({
    hs_code: z
      .string({
        required_error: errorMessage.required_hs_code,
        invalid_type_error: errorMessage.required_hs_code,
      })
      .regex(/^\d{10}$/, { message: errorMessage.hs_code_length_10 })
      .refine((hs_code) => hs_code.length !== 0, { message: errorMessage.required_hs_code }),
    product: ProductIntlSchema,
    tpl_fee: tplFeeSchema,
  }),
});

export function getStoresProductSchema<T extends MODE>(
  mode: T,
): T extends typeof MODE.NEW
  ? typeof creatorProductSchema
  : T extends typeof MODE.EDIT
    ? typeof creatorProductEditSchema
    : T extends typeof MODE.EDIT_TPL
      ? typeof creatorProductTplSchema
      : T extends typeof MODE.EDIT_TPL_INTL
        ? typeof creatorProductTplIntlSchema
        : never;

export function getStoresProductSchema(
  mode: MODE,
):
  | typeof creatorProductSchema
  | typeof creatorProductEditSchema
  | typeof creatorProductTplSchema
  | typeof creatorProductTplIntlSchema {
  switch (mode) {
    case MODE.NEW:
      return creatorProductSchema;
    case MODE.EDIT:
      return creatorProductEditSchema;
    case MODE.EDIT_TPL:
      return creatorProductTplSchema;
    case MODE.EDIT_TPL_INTL:
      return creatorProductTplIntlSchema;
    default:
      return creatorProductSchema;
  }
}
